import { useEffect, useState } from "react";
import { Calendar, ExternalLink, Timer, Trophy } from "lucide-react";
import { useUiTexts } from "@/lib/uiTexts";

const STATUS_STYLES = {
  "VICTOIRE": "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  "QUALIFIÉ": "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  "DÉFAITE": "border-red-500/40 bg-red-500/10 text-red-400",
  "À VENIR": "border-orange-500/40 bg-orange-500/10 text-orange-400",
};

function MiniCountdown({ datetime, matchId, prefix }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ts = new Date(datetime).getTime();
  if (Number.isNaN(ts) || ts <= now) return null;
  const diff = ts - now;
  const d = Math.floor(diff / 86400000);
  const h = String(Math.floor((diff % 86400000) / 3600000)).padStart(2, "0");
  const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
  const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
  const label = d > 0 ? `J-${d} ${h}:${m}:${s}` : `${h}:${m}:${s}`;
  return (
    <div className="mt-3 flex items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 px-3 py-2"
      data-testid={`match-countdown-mini-${matchId}`}>
      <Timer className="h-4 w-4 shrink-0 text-orange-500" />
      <span className="font-mono2 text-sm font-bold text-orange-400">{prefix} {label}</span>
    </div>
  );
}

export default function MatchCard({ match }) {
  const isTft = match.game === "Teamfight Tactics";
  const texts = useUiTexts();
  return (
    <div data-testid={`match-card-${match.id}`} className="card-tactical p-6 transition-transform hover:-translate-y-1">
      <div className="mb-4 flex items-center justify-between gap-2">
        <span className={`rounded-md border px-2 py-1 text-[11px] font-bold uppercase tracking-widest ${isTft ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-300" : "border-orange-500/40 bg-orange-500/10 text-orange-400"}`}>
          {match.game}
        </span>
        <span className={`rounded-md border px-2 py-1 text-[11px] font-bold uppercase tracking-widest ${STATUS_STYLES[match.status] || STATUS_STYLES["À VENIR"]}`}>
          {match.status}
        </span>
      </div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{match.event}</p>
      <h3 className="mt-1 text-2xl font-bold uppercase tracking-tight">
        Emisphere <span className="text-orange-500">vs</span> {match.opponent}
      </h3>
      {match.opgg_url && (
        <a href={match.opgg_url} target="_blank" rel="noreferrer" data-testid={`match-opgg-${match.id}`}
          className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-cyan-300 transition-colors hover:text-cyan-200">
          {texts.match_opponent_link.replace("{opponent}", match.opponent)} <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
      <p className="font-mono2 mt-3 text-3xl font-bold text-white">{match.score}</p>
      {match.status === "À VENIR" && match.match_datetime && (
        <MiniCountdown datetime={match.match_datetime} matchId={match.id} prefix={texts.match_countdown_prefix} />
      )}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-400">
        <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-orange-500" /> {match.date}</span>
        {match.mvp && <span className="flex items-center gap-1.5"><Trophy className="h-4 w-4 text-orange-500" /> {texts.match_mvp_label} : {match.mvp}</span>}
        {match.stream_url && (
          <a href={match.stream_url} target="_blank" rel="noreferrer" data-testid={`match-stream-${match.id}`} className="flex items-center gap-1.5 font-semibold text-orange-400 hover:text-orange-300">
            {texts.match_stream_link} <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}
